// Input-variation report: rerun the full calculation while one party's income input moves across a range.
import type { Case, CaseResult, PartyIndex } from '@divorceos/schema';

export interface VariationStep {
  /** Change from the entered value, percent (e.g. -30 .. +30). */
  pct: number;
  /** Monthly value of the varied input at this step (never negative). */
  value: number;
}

/**
 * Steps from -range% to +range% of `base` in `step`% increments, always including 0 (the case as entered).
 * `step` is clamped to at least 1 and `range` to at least `step` and at most 100; at most 41 steps.
 */
export function variationSteps(base: number, stepPct: number, rangePct: number): VariationStep[] {
  const step = Math.max(1, Math.abs(Number.isFinite(stepPct) ? stepPct : 10));
  const range = Math.min(100, Math.max(step, Math.abs(Number.isFinite(rangePct) ? rangePct : 30)));
  const n = Math.min(20, Math.floor(range / step + 1e-9));
  const out: VariationStep[] = [];
  for (let i = -n; i <= n; i++) {
    const pct = Math.round(i * step * 1000) / 1000;
    out.push({ pct, value: Math.max(0, base * (1 + pct / 100)) });
  }
  return out;
}

/** Which income input the report varies. */
export type VariedInput = 'wages' | 'selfEmployment';
export const VARIED_INPUT_LABEL: Record<VariedInput, string> = { wages: 'Wages + salary', selfEmployment: 'Self-employment income' };

/** Copy of `c` with party `p`'s input set to `monthly`. */
export function withInput(c: Case, p: PartyIndex, input: VariedInput, monthly: number): Case {
  const next = structuredClone(c);
  next.parties[p][input] = monthly;
  return next;
}

export interface VariationRow extends VariationStep { result: CaseResult | null; error: string | null }

/** Runs `compute` at every step. Errors are captured per row so one bad step does not hide the table. */
export function runVariation(c: Case, p: PartyIndex, input: VariedInput, stepPct: number, rangePct: number, compute: (c: Case) => CaseResult): VariationRow[] {
  return variationSteps(c.parties[p][input], stepPct, rangePct).map((s) => {
    try {
      return { ...s, result: compute(withInput(c, p, input, s.value)), error: null };
    } catch (e) {
      return { ...s, result: null, error: e instanceof Error ? e.message : String(e) };
    }
  });
}
